import {
  DayOfWeek,
  IsoFields,
  LocalDate,
  TemporalAdjusters,
} from "@js-joda/core";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { useMemo } from "react";
import styled from "styled-components";

export interface PeriodNavigatorProps {
  currentDate: LocalDate;
  mode: "month" | "year" | "week";
  onChange: (nextDate: LocalDate) => void;
  showSubLabel?: boolean;
  showYear?: boolean;
  allowPrev?: boolean;
  allowNext?: boolean;
  prevAriaLabel?: string;
  nextAriaLabel?: string;
}

/** Navigation header for month/year period selection. */
export const PeriodNavigator = ({
  currentDate,
  mode,
  onChange,
  showSubLabel = false,
  showYear = true,
  allowPrev = true,
  allowNext = true,
  prevAriaLabel = "Previous period",
  nextAriaLabel = "Next period",
}: PeriodNavigatorProps): JSX.Element => {
  const getMonthName = (date: LocalDate): string => {
    const jsDate = new Date(date.year(), date.monthValue() - 1, 1);
    const raw = new Intl.DateTimeFormat(undefined, { month: "long" }).format(
      jsDate,
    );
    return raw.length > 0 ? raw[0].toUpperCase() + raw.slice(1) : raw;
  };

  const monthName = useMemo(() => getMonthName(currentDate), [currentDate]);

  const weekStart = useMemo(
    () => currentDate.with(TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY)),
    [currentDate],
  );
  const weekEnd = useMemo(() => weekStart.plusDays(6), [weekStart]);
  const weekNumber = useMemo(
    () => currentDate.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR),
    [currentDate],
  );

  const label = useMemo(() => {
    if (mode === "year") return `${currentDate.year()}`;
    if (mode === "week") return `Week ${weekNumber}`;
    return `${monthName}${showYear ? ` ${currentDate.year()}` : ""}`;
  }, [currentDate, mode, monthName, showYear, weekNumber]);

  const subLabel = useMemo(() => {
    if (!showSubLabel) return undefined;
    if (mode === "month") {
      return `1 - ${currentDate.lengthOfMonth()} ${monthName}`;
    }
    if (mode === "week") {
      const startMonth = getMonthName(weekStart);
      const endMonth = getMonthName(weekEnd);
      if (weekStart.monthValue() === weekEnd.monthValue()) {
        return `${weekStart.dayOfMonth()} - ${weekEnd.dayOfMonth()} ${startMonth} ${weekEnd.year()}`;
      }
      return `${weekStart.dayOfMonth()} ${startMonth} - ${weekEnd.dayOfMonth()} ${endMonth} ${weekEnd.year()}`;
    }
    return undefined;
  }, [currentDate, mode, monthName, showSubLabel, weekEnd, weekStart]);

  const prevDate =
    mode === "year"
      ? currentDate.minusYears(1)
      : mode === "week"
        ? currentDate.minusWeeks(1)
        : currentDate.minusMonths(1);

  const nextDate =
    mode === "year"
      ? currentDate.plusYears(1)
      : mode === "week"
        ? currentDate.plusWeeks(1)
        : currentDate.plusMonths(1);

  const handlePrev = (): void => {
    if (!allowPrev) return;
    onChange(prevDate);
  };

  const handleNext = (): void => {
    if (!allowNext) return;
    onChange(nextDate);
  };

  return (
    <Container>
      <NavButton
        type="button"
        aria-label={prevAriaLabel}
        onClick={handlePrev}
        disabled={!allowPrev}
      >
        <CustomIcon name="v2/left-chevron" size="24px" color={ColorV2.Blue2} />
      </NavButton>
      <TextBlock>
        <Label>{label}</Label>
        {subLabel && <SubLabel>{subLabel}</SubLabel>}
      </TextBlock>
      <NavButton
        type="button"
        aria-label={nextAriaLabel}
        onClick={handleNext}
        disabled={!allowNext}
      >
        <CustomIcon name="v2/right-chevron" size="24px" color={ColorV2.Blue2} />
      </NavButton>
    </Container>
  );
};

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-radius: 999px;
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${ColorV2.Blue2};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const Label = styled.div`
  ${TypographyV2.h3};
  color: ${ColorV2.Blue1};
`;

const SubLabel = styled.div`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
`;
